import {
  WEBSITE_ISSUE_CODES,
  WebsiteMediaUploadError,
  WebsiteTemplateServiceError,
  websiteMediaUploadMessageKeySuffix,
  websiteRegionIssueMessageKey,
  type WebsiteIssueCode,
  type WebsiteMediaUploadErrorKey,
  type WebsiteRegionFieldState,
  type WebsiteSlotSchema,
  type WebsiteTemplateServiceErrorCode,
} from '@dukanti/shared';

const DIALOG = 'admin.websiteTemplates.dialog';
const ERRORS = 'admin.websiteTemplates.errors';
const READINESS = 'admin.websiteTemplates.readiness';
const ISSUE = 'admin.websiteTemplates.issue';

export const SAVE_FAILED_KEY = `${DIALOG}.saveFailed`;

/** True for the error of a draft that another session changed first. */
export function isStaleDraft(error: unknown): boolean {
  return error instanceof WebsiteTemplateServiceError && error.code === 'stale_draft';
}

/**
 * What a failed Save says. An issue the server found in a value has its own
 * message; a lost connection, a refused permission or anything unknown is the
 * plain "couldn't save". `stale_draft` has its own dialog and is not here.
 */
export function saveErrorKey(error: unknown): string {
  if (!(error instanceof WebsiteTemplateServiceError)) return SAVE_FAILED_KEY;
  switch (error.code) {
    case 'permission_denied':
    case 'preview_expired':
    case 'network':
    case 'unknown':
      return SAVE_FAILED_KEY;
    default:
      return websiteRegionIssueMessageKey(error.code);
  }
}

/** The field a failed Save points at, with the message under it. Null when the server named no field. */
export function serverFieldIssue(error: unknown): { slotId: string; key: string } | null {
  if (!(error instanceof WebsiteTemplateServiceError) || error.slotId === null) return null;
  const code = WEBSITE_ISSUE_CODES.find((candidate) => candidate === error.code);
  return code ? { slotId: error.slotId, key: websiteRegionIssueMessageKey(code) } : null;
}

export const MEDIA_ALT_REQUIRED_KEY = 'admin.websiteTemplates.media.altRequired';

function authoredAltIsEmpty(state: WebsiteRegionFieldState | undefined): boolean {
  if (state === undefined || state.mode !== 'authored' || !('v' in state.value)) return false;
  const inner: unknown = state.value.v;
  return (
    typeof inner === 'object' &&
    inner !== null &&
    typeof (inner as { alt?: unknown }).alt === 'string' &&
    (inner as { alt: string }).alt.trim() === ''
  );
}

/**
 * Epic #228 P4: the message under a field the region schema refused (RULE 0).
 * A non-decorative image without a description is the one case with its own
 * sentence: the value stays in the dialog, unsaved, until the description is
 * typed. Everything else keeps the schema's own key.
 */
export function regionFieldIssueKey(
  slot: WebsiteSlotSchema,
  state: WebsiteRegionFieldState | undefined,
  code: string,
  fallbackKey: string,
): string {
  if (slot.type === 'media' && slot.decorative !== true && authoredAltIsEmpty(state)) {
    return MEDIA_ALT_REQUIRED_KEY;
  }
  const known = WEBSITE_ISSUE_CODES.find((candidate) => candidate === code);
  return known ? websiteRegionIssueMessageKey(known) : fallbackKey;
}

/** Epic #228 P4: the message of a slot the server refused and the queue kept (PD7). */
export function rejectedFieldIssueKey(code: WebsiteTemplateServiceErrorCode): string {
  const known = WEBSITE_ISSUE_CODES.find((candidate) => candidate === code);
  return known ? websiteRegionIssueMessageKey(known) : SAVE_FAILED_KEY;
}

/** Epic #228 P5: the message of a link slot whose value was refused for its shape. */
export const DESTINATION_INVALID_KEY = 'admin.destination.invalid';

/**
 * Epic #228 P5: the message under a destination field. A link value the
 * schema or the server refused as a value (`invalid_slot`, `structure_override`,
 * …, all "This value can't be saved") reads as "Choose where this link goes";
 * every other key (rate limit, retired slot, …) is kept.
 */
export function destinationIssueKey(issueKey: string | undefined): string | undefined {
  return issueKey === `${ERRORS}.invalidValue` ? DESTINATION_INVALID_KEY : issueKey;
}

export function stageErrorKey(error: unknown): string {
  return error instanceof WebsiteTemplateServiceError && error.code === 'template_unavailable'
    ? `${ERRORS}.templateUnavailable`
    : `${ERRORS}.generic`;
}

export function publishErrorKey(error: unknown): string {
  return isStaleDraft(error)
    ? 'admin.websiteTemplates.publish.stale'
    : 'admin.websiteTemplates.publish.failed';
}

/** The sentence of one readiness issue. `{{label}}` names the region. */
export function readinessIssueKey(code: WebsiteIssueCode): string {
  switch (code) {
    case 'missing_merchant_value':
      return `${READINESS}.missingMerchantValue`;
    case 'missing_required':
      return `${READINESS}.missingRequired`;
    case 'invalid_media':
      return `${READINESS}.invalidMedia`;
    case 'invalid_reference':
    case 'cross_store_reference':
      return `${READINESS}.invalidReference`;
    case 'missing_store_logo':
      return `${READINESS}.missingLogo`;
    default:
      return `${ERRORS}.invalidValue`;
  }
}

/**
 * The sentence of a readiness warning (epic #228 P1): the issue does not stop
 * the publish, so the copy says what is worth fixing, not what must be fixed.
 * A code without warning copy falls back to its blocking sentence. `named`
 * says the row has a region name for `{{label}}`.
 */
export function readinessWarningKey(code: WebsiteIssueCode, named = false): string {
  switch (code) {
    case 'missing_store_logo':
      return `${ISSUE}.missingStoreLogo.warning`;
    case 'invalid_reference':
    case 'cross_store_reference':
      // Several dead links must be told apart: name the region when there is one.
      return named ? `${ISSUE}.invalidReference.warningNamed` : `${ISSUE}.invalidReference.warning`;
    case 'missing_merchant_value':
      return `${ISSUE}.missingMerchantValue.warning`;
    default:
      return readinessIssueKey(code);
  }
}

/** The message of a failed upload. `aborted` is the merchant's own doing and says nothing. */
export function mediaUploadErrorKey(error: unknown): string | null {
  if (error instanceof WebsiteMediaUploadError) {
    if (error.key === 'aborted') return null;
    return `admin.websiteTemplates.media.error.${websiteMediaUploadMessageKeySuffix(error.key)}`;
  }
  return 'admin.websiteTemplates.media.error.generic';
}

export function mediaPrecheckErrorKey(key: WebsiteMediaUploadErrorKey): string {
  return `admin.websiteTemplates.media.error.${websiteMediaUploadMessageKeySuffix(key)}`;
}
